// src/pos-app/surfaces/cashier/CatalogBrowse.js
//
// F12a-N Task 2 — the catalog browse region of the native Sell surface
// (design contract: docs/mockups/f12a-n/cashier-sell.html, browse state).
// Search field, category chips ("All" + one per category with image/icon),
// contextual subcategory chips, and the product tile grid. The grid is the
// ONLY scroller in this region (HARD no-scroll rule, spec §6).
//
// Business logic is REUSED, never forked:
// - All catalog state comes from the parent's useProductCatalog instance
//   (SellSurface owns it so the Task 3 cart shares the same data).
// - Grid derivation copies the legacy page exactly (CashierSalesPage.js
//   1748-1761): a selected category reuses the hook's filtered list; the
//   "All" view filters the full product list by the same search semantics
//   (name / product_code / subcategory, case-insensitive).
// - Stock display copies legacy 2899-2911 including the -9999 = infinite
//   rule and the "…" placeholder while auto stock is still being computed.
// - Adds go through the parent's onAddProduct (legacy handleAddProduct,
//   707-716: dining-pill nudge when no option is selected, then
//   useOrderBuilder.handleProductWithAddons — which opens the REUSED
//   AddonSelectionModal for products with available_addons).
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    useLanguage, getEntityTranslation, getProductName, getTransliteration, translateDynamic,
} from '../../../context/LanguageContext';
import useIsMobile from '../../../hooks/useIsMobile';
import { getCategoryIcon } from '../../../constants/categoryIcons';
import { byCatalogueOrder } from '../../../utils/catalogueOrder';
import { catalogueOrderService } from '../../../services/api';
import ManagerGate from '../../../components/pos/ManagerGate';
import { renderEntityImage } from '../../../utils/imageUtils';
import AddonSelectionModal from '../../../components/pos/modals/AddonSelectionModal';

// Tokens only (--c-* theme vars, light + dark). Logical properties keep the
// region RTL-correct. Chips keep the mockup's 38/30px visual height; the
// ::after overlay extends every chip's hit area to ≥44px.
const CSS = `
.cbr-root{flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden;}

/* search row — mockup .browse-head / .search */
.cbr-head{flex:none;display:flex;gap:10px;align-items:center;margin-bottom:12px;}
.cbr-search{flex:1;display:flex;align-items:center;gap:9px;height:44px;padding:0 14px;
  background:var(--c-surface);border:1px solid var(--c-border);border-radius:12px;}
.cbr-search svg{color:var(--c-muted);flex:none;}
.cbr-search input{flex:1;min-width:0;border:none;background:none;outline:none;
  font-family:inherit;font-size:13.5px;color:var(--c-ink);}
.cbr-search input::placeholder{color:var(--c-muted);}
.cbr-search-x{position:relative;flex:none;width:28px;height:28px;border-radius:8px;border:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;background:var(--c-faint);color:var(--c-muted);}
.cbr-search-x::after{content:"";position:absolute;inset:-8px;}

/* category chips — mockup .chips / .chip */
.cbr-chips{flex:none;display:flex;gap:8px;overflow-x:auto;padding-bottom:4px;margin-bottom:6px;scrollbar-width:none;}
.cbr-chips::-webkit-scrollbar{display:none;}
.cbr-chip{position:relative;flex:none;display:inline-flex;align-items:center;gap:7px;height:38px;padding:0 15px;
  border-radius:999px;font-family:inherit;font-size:12.5px;font-weight:700;cursor:pointer;
  background:var(--c-surface);border:1px solid var(--c-border);color:var(--c-slate);
  transition:border-color .12s, background .12s;}
/* Invisible tap-target extender: the chip row scrolls horizontally and the
   chips are only 38px tall, so this reclaims 4px above and below.
   Scoped OUT of reorder mode deliberately. In reorder mode the chip is a
   <div> wrapping two arrow <button>s, and this pseudo-element — being
   absolutely positioned, and therefore painted above its static siblings —
   would sit on top of them and swallow every tap. That is exactly what it
   did: the arrows rendered, looked enabled, and did nothing. A reorder-mode
   chip is not tappable itself (cursor:default), so it needs no extender. */
.cbr-chip:not(.cbr-chip-reorder)::after{content:"";position:absolute;inset-block:-4px;inset-inline:0;}
.cbr-chip:not(.active):hover{border-color:var(--c-greenBorder);}
.cbr-chip.active{background:var(--c-ink);color:var(--c-bg);border-color:var(--c-ink);}
/* Reorder mode: the chip keeps its footprint so the row does not reflow when
   the mode is entered, but gains a left/right control either side. Arrows,
   not drag — same reasoning as the product tiles: unambiguous on a tablet and
   usable under assistive tech. */
.cbr-chip-reorder{gap:4px;padding:0 6px;cursor:default;}
.cbr-chip-label{display:inline-flex;align-items:center;gap:7px;padding:0 4px;}
.cbr-chip-move{display:inline-flex;align-items:center;justify-content:center;
  width:26px;height:26px;border-radius:7px;cursor:pointer;font-size:10px;
  border:1px solid var(--c-border);background:var(--c-surface);color:var(--c-muted);}
.cbr-chip-move:disabled{opacity:.35;cursor:default;}

/* A subchip and its reorder controls travel together, so the arrows stay
   attached to their chip as the row scrolls. */
.cbr-subchip-wrap { display: inline-flex; align-items: center; gap: 2px; }

/* subcategory chips — mockup .subchips / .subchip */
.cbr-subchips{flex:none;display:flex;gap:6px;overflow-x:auto;margin-bottom:10px;scrollbar-width:none;}
.cbr-subchips::-webkit-scrollbar{display:none;}
.cbr-subchip{position:relative;flex:none;display:inline-flex;align-items:center;gap:6px;height:30px;padding:0 12px;
  border-radius:999px;font-family:inherit;font-size:11.5px;font-weight:700;cursor:pointer;
  background:var(--c-faint);border:1px solid var(--c-border);color:var(--c-muted);}
.cbr-subchip::after{content:"";position:absolute;inset-block:-7px;inset-inline:0;}
.cbr-subchip.active{color:var(--c-green);background:var(--c-greenLight);border-color:var(--c-greenBorder);}

/* product grid — the ONLY scroller in this region (mockup .grid / .tile) */
.cbr-grid{flex:1;min-height:0;overflow-y:auto;align-content:start;
  display:grid;grid-template-columns:repeat(auto-fill,minmax(148px,1fr));gap:10px;padding-bottom:6px;}
.cbr-tile{position:relative;border-radius:14px;background:var(--c-surface);border:1px solid var(--c-border);
  padding:10px;text-align:start;display:flex;flex-direction:column;gap:8px;min-height:130px;cursor:pointer;
  font-family:inherit;box-shadow:0 1px 3px rgba(15,23,42,.06);
  transition:transform .12s, box-shadow .12s, border-color .12s;}
.cbr-tile:not(.oos):hover{transform:translateY(-1px);border-color:var(--c-greenBorder);
  box-shadow:0 6px 16px color-mix(in srgb, var(--c-green) 14%, transparent);}
.cbr-media{flex:none;height:52px;border-radius:11px;overflow:hidden;display:flex;align-items:center;justify-content:center;
  font-size:19px;font-weight:800;color:var(--c-green);
  background:color-mix(in srgb, var(--c-green) 10%, var(--c-faint));}
.cbr-media.photo{background:var(--c-faint);}
.cbr-media.photo img{width:100%;height:100%;object-fit:cover;}
.cbr-media .emoji{font-size:24px;line-height:1;}
.cbr-reorder-bar{display:flex;justify-content:flex-end;padding:0 2px 6px;}
.cbr-reorder-btn{border:1px solid var(--c-border);background:var(--c-surface);color:var(--c-muted);
  border-radius:999px;padding:5px 13px;font-family:inherit;font-size:11.5px;font-weight:800;
  cursor:pointer;letter-spacing:.2px;}
.cbr-reorder-btn.active{background:var(--c-green);border-color:var(--c-green);color:#fff;}
.cbr-move{position:absolute;inset-block-start:4px;inset-inline-end:4px;display:flex;flex-direction:column;gap:3px;}
.cbr-move button{width:30px;height:30px;border-radius:8px;cursor:pointer;
  border:1px solid var(--c-border);background:var(--c-surface);color:var(--c-ink);
  font-size:14px;font-weight:800;line-height:1;display:flex;align-items:center;justify-content:center;}
.cbr-move button:disabled{opacity:.35;cursor:default;}
.cbr-tile-name{font-size:13px;font-weight:700;line-height:1.25;flex:1;color:var(--c-ink);}
.cbr-tile-foot{display:flex;align-items:center;justify-content:space-between;gap:6px;}
.cbr-tile-price{font-size:14px;font-weight:800;color:var(--c-ink);font-variant-numeric:tabular-nums;white-space:nowrap;}
.cbr-tile-stock{font-size:10.5px;font-weight:700;color:var(--c-muted);background:var(--c-faint);
  border:1px solid var(--c-border);border-radius:999px;padding:2px 8px;
  font-variant-numeric:tabular-nums;white-space:nowrap;}
.cbr-tile.oos{opacity:.5;cursor:not-allowed;}
.cbr-tile.oos .cbr-tile-stock{color:var(--c-red);background:var(--c-redLight);border-color:var(--c-redBorder);}
.cbr-none{grid-column:1/-1;text-align:center;padding:24px 0;font-size:13px;font-weight:600;color:var(--c-muted);}

/* ── F15 part 2 — phone catalogue ────────────────────────────────────────
   Three deliberate differences from the tablet, all scoped to ≤768px so the
   tablet grid is byte-identical to before.

   1. ONE chip row, not two. The tablet stacks category chips over contextual
      subcategory chips; both scroll horizontally. Two stacked horizontal
      scrollers inside a vertical scroll region is a gesture-conflict
      anti-pattern (ui-ux-pro-max "gesture-conflicts", High) and it is why
      both rows clipped mid-word on a phone ("Snacks", "Iced Coffee"). The
      category collapses to a picker button that opens a sheet; only the
      subchips scroll, with trailing padding and an edge fade so the last
      chip cannot be cut off mid-glyph.
   2. No tile media. Product images cost ~78px of every tile on a screen
      with ~500px of grid. Dropping them takes the tile from ~150px to
      ~72px — roughly 5-6 products visible to ~12 — and removes the
      letter-placeholder problem at its source for tenants with no photos.
   3. No reorder bar. Rearranging a menu is a seated, two-handed manager
      task; it stays on the tablet. */
@media (max-width:768px){
  .cbr-catrow{flex:none;display:flex;align-items:center;gap:8px;margin-bottom:10px;min-width:0;}
  .cbr-catpick{position:relative;flex:none;display:inline-flex;align-items:center;gap:7px;
    min-height:44px;padding:0 13px;border-radius:999px;cursor:pointer;font-family:inherit;
    font-size:12.5px;font-weight:800;background:var(--c-ink);color:var(--c-bg);
    border:1px solid var(--c-ink);max-width:52%;}
  .cbr-catpick-l{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0;}
  .cbr-catpick svg{flex:none;}

  /* The subchip scroller fills the rest of the row. The mask fades the
     trailing edge so a partially-scrolled chip reads as "more this way"
     rather than as a rendering fault. */
  .cbr-subchips{flex:1;min-width:0;margin-bottom:0;padding-inline-end:14px;
    scroll-snap-type:x proximity;
    -webkit-mask-image:linear-gradient(to right,#000 calc(100% - 22px),transparent 100%);
    mask-image:linear-gradient(to right,#000 calc(100% - 22px),transparent 100%);}
  [dir="rtl"] .cbr-subchips{
    -webkit-mask-image:linear-gradient(to left,#000 calc(100% - 22px),transparent 100%);
    mask-image:linear-gradient(to left,#000 calc(100% - 22px),transparent 100%);}
  .cbr-subchip{min-height:44px;scroll-snap-align:start;font-size:12.5px;padding:0 14px;}
  .cbr-subchip::after{inset-block:0;}

  .cbr-tile{min-height:72px;gap:6px;padding:9px 10px;}
  .cbr-tile-name{font-size:13.5px;}

  /* Category sheet — scrim + sheet portaled to document.body, same reason
     CartPanel.js:40-51 documents: a backdrop-filter ancestor would otherwise
     capture these position:fixed layers. */
  .cbr-scrim{position:fixed;inset:0;z-index:40;
    background:color-mix(in srgb, var(--c-ink) 45%, transparent);}
  .cbr-sheet{position:fixed;inset-inline:0;bottom:0;z-index:50;max-height:72%;
    display:flex;flex-direction:column;overflow:hidden;background:var(--c-surface);
    border-radius:22px 22px 0 0;padding-bottom:var(--safe-bottom);
    box-shadow:0 -18px 50px color-mix(in srgb, var(--c-ink) 22%, transparent);
    animation:cbrSheetIn .28s cubic-bezier(.32,.72,0,1);}
  @keyframes cbrSheetIn{from{transform:translateY(100%);}to{transform:translateY(0);}}
  @media (prefers-reduced-motion: reduce){.cbr-sheet{animation:none;}}
  .cbr-sheet-head{flex:none;display:flex;align-items:center;justify-content:space-between;
    gap:10px;padding:14px 18px 10px;
    border-bottom:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);}
  .cbr-sheet-title{font-size:15px;font-weight:800;color:var(--c-ink);}
  .cbr-sheet-x{width:44px;height:44px;border:none;background:none;cursor:pointer;
    color:var(--c-muted);display:flex;align-items:center;justify-content:center;}
  .cbr-sheet-body{flex:1;min-height:0;overflow-y:auto;padding:8px 12px 16px;}
  .cbr-sheet-row{width:100%;min-height:52px;display:flex;align-items:center;gap:11px;
    padding:0 14px;border-radius:13px;border:1px solid transparent;background:none;
    cursor:pointer;font-family:inherit;font-size:14px;font-weight:700;
    color:var(--c-ink);text-align:start;}
  .cbr-sheet-row.active{background:var(--c-greenLight);border-color:var(--c-greenBorder);
    color:var(--c-green);}
}
`;

const SearchIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
    </svg>
);

const ClearIcon = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 6 6 18M6 6l12 12" />
    </svg>
);

// Phone category-picker affordance (F15 part 2).
const ChevronDownIcon = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m6 9 6 6 6-6" />
    </svg>
);

// Tile media per mockup: photo cover when the product has an image URL,
// emoji when image_url is an emoji, else a soft brand-tinted initial block.
const TileMedia = ({ imageUrl, displayName }) => {
    if (imageUrl && imageUrl.startsWith('http')) {
        return (
            <div className="cbr-media photo" aria-hidden="true">
                <img src={imageUrl} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
        );
    }
    if (imageUrl) {
        return <div className="cbr-media" aria-hidden="true"><span className="emoji">{imageUrl}</span></div>;
    }
    return <div className="cbr-media" aria-hidden="true">{(displayName || '?').trim().charAt(0).toUpperCase()}</div>;
};

export default function CatalogBrowse({ catalog, builder, currency, onAddProduct }) {
    const { t, language, translationDetails } = useLanguage();
    // F15 part 2 — the phone catalogue is deliberately REDUCED, not shrunk.
    // See the CSS block above for the reasoning behind each of the three
    // differences (no tile media, no reorder bar, one chip row not two).
    const isMobile = useIsMobile();
    const [catSheetOpen, setCatSheetOpen] = useState(false);

    // This component owns the category sheet's dismissal, the same way
    // CartPanel owns Escape for its own promo/discount/notes layers rather
    // than lifting that state into SellSurface's chain. Leaving mobile also
    // closes it, so the sheet can never be left open over a tablet layout
    // that has no button to reopen it (mirrors SellSurface's cart sheet).
    useEffect(() => {
        if (!catSheetOpen) return undefined;
        if (!isMobile) { setCatSheetOpen(false); return undefined; }
        const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setCatSheetOpen(false); } };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [catSheetOpen, isMobile]);
    const {
        products, filteredProducts,
        categories, categoryObjects, categoryIcons,
        selectedCategory, setSelectedCategory,
        subcategories, selectedSubcategory, setSelectedSubcategory,
        bulkCalculatedStock,
        productSearch, setProductSearch,
        loadCatalog,
    } = catalog;

    // ---- reorder mode (client request 2026-08-26) ----------------------
    // Rearranging the menu is a manager action, so the control opens the
    // shared ManagerGate first and only enters the mode on approval.
    //
    // Move-up / move-down buttons rather than true touch drag: on a tablet
    // these are unambiguous, they cannot be mistaken for a tap that adds an
    // item to a customer's order, and they work identically under assistive
    // tech. A drag gesture is a reasonable later upgrade.
    //
    // The whole new order is persisted in ONE call — a half-applied reorder
    // would leave the menu in an arrangement nobody chose.
    const [reorderGateOpen, setReorderGateOpen] = useState(false);
    const [reordering, setReordering] = useState(false);
    const [localOrder, setLocalOrder] = useState(null);
    // Category order is held separately from the product order: the two are
    // different entities server-side, and reordering a chip must not renumber
    // the products underneath it.
    const [localCategoryOrder, setLocalCategoryOrder] = useState(null);
    const [localSubcategoryOrder, setLocalSubcategoryOrder] = useState(null);

    const move = async (productCode, delta) => {
        const codes = (localOrder || gridProducts.map((p) => p.product_code)).slice();
        const from = codes.indexOf(productCode);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= codes.length) return;
        codes.splice(to, 0, codes.splice(from, 1)[0]);
        setLocalOrder(codes);
        try {
            await catalogueOrderService.reorder('products', codes);
        } catch (err) {
            console.error('Failed to persist menu order:', err);
        }
    };

    // Same shape as `move`, on the chip row. delta is -1/+1 along a horizontal
    // scroller, so the buttons read as left/right rather than up/down.
    const moveCategory = async (categoryName, delta) => {
        const names = (localCategoryOrder || categories).slice();
        const from = names.indexOf(categoryName);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= names.length) return;
        names.splice(to, 0, names.splice(from, 1)[0]);
        setLocalCategoryOrder(names);
        // The endpoint keys categories by id, not name — a name is not unique
        // enough to renumber on. A category with no resolvable id is dropped
        // rather than sent as undefined, which would 404 the whole call and
        // lose the entire reorder.
        const ids = names
            .map((name) => categoryObjects[name]?.id)
            .filter((id) => id !== undefined && id !== null);
        if (ids.length !== names.length) {
            console.error('Skipping category reorder: some categories have no id');
            return;
        }
        try {
            await catalogueOrderService.reorder('categories', ids);
        } catch (err) {
            console.error('Failed to persist category order:', err);
        }
    };

    // Subcategories reorder exactly like categories: same manager gate, same
    // horizontal chip row, same endpoint. They were the one level of the menu
    // a cashier could not arrange from the till — the portal had it, the POS
    // did not.
    const moveSubcategory = async (subcategoryName, delta) => {
        const current = localSubcategoryOrder || subcategories;
        const from = current.findIndex((s) => s.name === subcategoryName);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= current.length) return;

        const next = current.slice();
        next.splice(to, 0, next.splice(from, 1)[0]);
        setLocalSubcategoryOrder(next);

        // Keyed by id, like the category reorder: a name is not unique enough
        // to renumber on, and one missing id would 404 the call and lose the
        // whole arrangement.
        const ids = next.map((s) => s.id).filter((id) => id !== undefined && id !== null);
        if (ids.length !== next.length) {
            console.error('Skipping subcategory reorder: some subcategories have no id');
            return;
        }
        try {
            await catalogueOrderService.reorder('subcategories', ids);
        } catch (err) {
            console.error('Failed to persist subcategory order:', err);
        }
    };

    // With the "All" chip gone there has to be a landing category, or the
    // cashier opens the till to an empty grid and no obvious next tap. Only
    // fires when nothing is selected, so it never overrides a real choice.
    useEffect(() => {
        if (!selectedCategory && categories.length > 0) {
            setSelectedCategory(categories[0]);
        }
    }, [selectedCategory, categories, setSelectedCategory]);

    // Products shown in the grid — legacy derivation (CashierSalesPage.js
    // 1748-1761) with the "All" branch retained only as the one-render
    // fallback before the effect above lands a category, and while a search
    // is running (search spans categories).
    const gridProducts = useMemo(() => {
        if (selectedCategory) return filteredProducts;
        let filtered = [...products].sort(byCatalogueOrder);
        if (productSearch.trim()) {
            const term = productSearch.toLowerCase().trim();
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(term) ||
                p.product_code.toLowerCase().includes(term) ||
                (p.subcategory && p.subcategory.toLowerCase().includes(term))
            );
        }
        return filtered;
    }, [selectedCategory, filteredProducts, products, productSearch]);

    // While reordering, render the arrangement the cashier is building rather
    // than waiting for a refetch — the list must not jump under their thumb.
    const displayProducts = useMemo(() => {
        if (!reordering || !localOrder) return gridProducts;
        const byCode = {};
        gridProducts.forEach((p) => { byCode[p.product_code] = p; });
        return localOrder.map((code) => byCode[code]).filter(Boolean);
    }, [reordering, localOrder, gridProducts]);

    // Same reason as displayProducts: show the arrangement being built, not
    // the one the server last sent. `filter` guards a category deleted on
    // another till mid-reorder.
    const displayCategories = useMemo(() => {
        if (!reordering || !localCategoryOrder) return categories;
        const known = new Set(categories);
        return localCategoryOrder.filter((name) => known.has(name));
    }, [reordering, localCategoryOrder, categories]);

    // Stock display — exact legacy rules (CashierSalesPage.js 2899-2911):
    // auto products read bulkCalculatedStock ("…" until computed); manual
    // products treat quantity === -9999 as infinite.
    const stockInfo = (product) => {
        if (product.stock_calculation_type === 'auto') {
            const stockData = bulkCalculatedStock[product.product_code];
            const calc = stockData ? stockData.available_stock : 0;
            return {
                display: stockData ? (calc === Infinity ? '∞' : calc) : '…',
                isOut: calc < 1,
                pending: !stockData, // legacy shows "..." (and disables) until computed
            };
        }
        return {
            display: product.quantity === -9999 ? '∞' : product.quantity,
            isOut: product.quantity < 1 && product.quantity !== -9999,
            pending: false,
        };
    };

    // Display-name chain identical to the legacy tiles (2913).
    const productLabel = (product) =>
        getProductName(product, language, translationDetails) ?? getTransliteration(product.name, language, t);

    const categoryLabel = (cat) =>
        getEntityTranslation(categoryObjects[cat], language, translationDetails) || translateDynamic(cat, t);

    const subcategoryLabel = (subcat) =>
        getEntityTranslation(subcat, language, translationDetails) || getTransliteration(subcat.name, language, t);

    return (
        <div className="cbr-root">
            <style>{CSS}</style>

            {/* Search */}
            <div className="cbr-head">
                <div className="cbr-search">
                    {SearchIcon}
                    <input
                        type="search"
                        value={productSearch}
                        placeholder={t('search_products_placeholder')}
                        aria-label={t('search_products_placeholder')}
                        onChange={(e) => setProductSearch(e.target.value)}
                    />
                    {productSearch && (
                        <button type="button" className="cbr-search-x" aria-label={t('clear')} onClick={() => setProductSearch('')}>
                            {ClearIcon}
                        </button>
                    )}
                </div>
            </div>

            {/* Reorder control — manager-gated (client request 2026-08-26).
                Not rendered at all on a phone (F15 part 2): rearranging a menu
                is a seated, two-handed manager task, and omitting it rather
                than hiding it with CSS keeps it out of the accessibility tree
                and the tab order too. */}
            {!isMobile && (
            <div className="cbr-reorder-bar">
                {reordering ? (
                    <button
                        type="button"
                        className="cbr-reorder-btn active"
                        data-testid="reorder-done"
                        onClick={async () => {
                            setReordering(false);
                            // Reload BEFORE clearing the local arrangement:
                            // `products` state still holds the pre-reorder
                            // order, so clearing first made the menu snap back
                            // until a page refresh (staging QC, 2026-08-26).
                            try { if (loadCatalog) await loadCatalog(); }
                            catch (err) { console.error('Failed to reload catalogue after reorder:', err); }
                            setLocalOrder(null);
                            // Cleared after the reload for the same reason as
                            // localOrder: `categories` still holds the old
                            // arrangement until loadCatalog resolves, so
                            // clearing first makes the chip row snap back.
                            setLocalCategoryOrder(null);
                        }}
                    >
                        {t('done', {}, 'Done')}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="cbr-reorder-btn"
                        data-testid="reorder-toggle"
                        onClick={() => setReorderGateOpen(true)}
                    >
                        {t('reorder_menu', {}, 'Reorder menu')}
                    </button>
                )}
            </div>
            )}

            {/* Category chips — one per category, in sort_order.
                The "All" chip is deliberately gone (client request
                2026-08-26): it rendered every product in one unusable wall.
                The SUBCATEGORY "All" below stays — that one means "everything
                in this category", which is a different and useful thing. */}
            {categories.length > 0 && !isMobile && (
                <div className="cbr-chips" role="group" aria-label={t('products')}>
                    {displayCategories.map((cat, catIndex) => (
                        reordering ? (
                            // In reorder mode the chip stops being a filter and
                            // becomes the thing being moved — tapping it must not
                            // switch category out from under the manager.
                            <div key={cat} className="cbr-chip cbr-chip-reorder">
                                <button
                                    type="button"
                                    className="cbr-chip-move"
                                    data-testid={`category-move-back-${cat}`}
                                    aria-label={`${categoryLabel(cat)}: ${t('move_left', {}, 'move left')}`}
                                    disabled={catIndex === 0}
                                    onClick={() => moveCategory(cat, -1)}
                                >&#9664;</button>
                                <span className="cbr-chip-label">
                                    {renderEntityImage(categoryObjects[cat]?.image_url, 20)
                                        || getCategoryIcon(categoryIcons[cat] || 'star', { size: 16, color: 'currentColor' })}
                                    {categoryLabel(cat)}
                                </span>
                                <button
                                    type="button"
                                    className="cbr-chip-move"
                                    data-testid={`category-move-fwd-${cat}`}
                                    aria-label={`${categoryLabel(cat)}: ${t('move_right', {}, 'move right')}`}
                                    disabled={catIndex === displayCategories.length - 1}
                                    onClick={() => moveCategory(cat, 1)}
                                >&#9654;</button>
                            </div>
                        ) : (
                            <button
                                key={cat}
                                type="button"
                                className={`cbr-chip${selectedCategory === cat ? ' active' : ''}`}
                                aria-pressed={selectedCategory === cat}
                                onClick={() => {
                                    if (cat !== selectedCategory) {
                                        setSelectedCategory(cat);
                                        setSelectedSubcategory(null);
                                        setProductSearch('');
                                    }
                                }}
                            >
                                {renderEntityImage(categoryObjects[cat]?.image_url, 20)
                                    || getCategoryIcon(categoryIcons[cat] || 'star', { size: 16, color: 'currentColor' })}
                                {categoryLabel(cat)}
                            </button>
                        )
                    ))}
                </div>
            )}

            {/* Subcategory chips — contextual (legacy 2867-2896).
                On a phone this row also hosts the category picker button, so
                the surface carries ONE horizontal scroller instead of two —
                see the @media block in CSS above. */}
            {(() => {
                const subchips = selectedCategory && subcategories.length > 0 ? (
                    <div className="cbr-subchips" role="group" aria-label={categoryLabel(selectedCategory)}>
                        <button
                            type="button"
                            className={`cbr-subchip${!selectedSubcategory ? ' active' : ''}`}
                            aria-pressed={!selectedSubcategory}
                            onClick={() => setSelectedSubcategory(null)}
                        >
                            {t('all')}
                        </button>
                        {(localSubcategoryOrder || subcategories).map((subcat, subIndex, subList) => (
                            <span key={subcat.id} className="cbr-subchip-wrap">
                                {reordering && (
                                    <button
                                        type="button"
                                        className="cbr-chip-move"
                                        data-testid={`subcategory-move-back-${subcat.name}`}
                                        aria-label={t('move_left', {}, 'Move left')}
                                        disabled={subIndex === 0}
                                        onClick={() => moveSubcategory(subcat.name, -1)}
                                    >
                                        ◀
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={`cbr-subchip${selectedSubcategory === subcat.name ? ' active' : ''}`}
                                    aria-pressed={selectedSubcategory === subcat.name}
                                    onClick={() => setSelectedSubcategory(selectedSubcategory === subcat.name ? null : subcat.name)}
                                >
                                    {subcat.image_url && renderEntityImage(subcat.image_url, 16)}
                                    {subcategoryLabel(subcat)}
                                </button>
                                {reordering && (
                                    <button
                                        type="button"
                                        className="cbr-chip-move"
                                        data-testid={`subcategory-move-fwd-${subcat.name}`}
                                        aria-label={t('move_right', {}, 'Move right')}
                                        disabled={subIndex === subList.length - 1}
                                        onClick={() => moveSubcategory(subcat.name, 1)}
                                    >
                                        ▶
                                    </button>
                                )}
                            </span>
                        ))}
                    </div>
                ) : null;

                if (!isMobile) return subchips;
                if (categories.length === 0) return subchips;

                return (
                    <div className="cbr-catrow">
                        <button
                            type="button"
                            className="cbr-catpick"
                            data-testid="category-picker"
                            aria-haspopup="dialog"
                            aria-expanded={catSheetOpen}
                            onClick={() => setCatSheetOpen(true)}
                        >
                            {getCategoryIcon(categoryIcons[selectedCategory] || 'star', { size: 15, color: 'currentColor' })}
                            <span className="cbr-catpick-l">{categoryLabel(selectedCategory)}</span>
                            {ChevronDownIcon}
                        </button>
                        {subchips}
                    </div>
                );
            })()}

            {/* Category sheet (phone only) — portaled to document.body so its
                position:fixed layers resolve against the viewport rather than
                a backdrop-filter ancestor (CartPanel.js:40-51 documents the
                same trap). */}
            {isMobile && catSheetOpen && createPortal(
                <>
                    <div
                        className="cbr-scrim"
                        data-testid="category-scrim"
                        onClick={() => setCatSheetOpen(false)}
                    />
                    <div className="cbr-sheet" role="dialog" aria-modal="true" aria-label={t('category')}>
                        <div className="cbr-sheet-head">
                            <span className="cbr-sheet-title">{t('category')}</span>
                            <button
                                type="button"
                                className="cbr-sheet-x"
                                aria-label={t('close')}
                                onClick={() => setCatSheetOpen(false)}
                            >
                                {ClearIcon}
                            </button>
                        </div>
                        <div className="cbr-sheet-body">
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    type="button"
                                    className={`cbr-sheet-row${selectedCategory === cat ? ' active' : ''}`}
                                    aria-pressed={selectedCategory === cat}
                                    onClick={() => {
                                        if (cat !== selectedCategory) {
                                            setSelectedCategory(cat);
                                            setSelectedSubcategory(null);
                                            setProductSearch('');
                                        }
                                        setCatSheetOpen(false);
                                    }}
                                >
                                    {renderEntityImage(categoryObjects[cat]?.image_url, 20)
                                        || getCategoryIcon(categoryIcons[cat] || 'star', { size: 18, color: 'currentColor' })}
                                    {categoryLabel(cat)}
                                </button>
                            ))}
                        </div>
                    </div>
                </>,
                document.body
            )}

            {/* Product grid — the only scroller */}
            <div className="cbr-grid">
                {displayProducts.map((product, index) => {
                    const { display, isOut, pending } = stockInfo(product);
                    const name = productLabel(product);
                    return (
                        <button
                            key={product.product_code}
                            type="button"
                            className={`cbr-tile${isOut ? ' oos' : ''}`}
                            disabled={isOut && !reordering}
                            aria-disabled={isOut && !reordering}
                            onClick={reordering ? undefined : () => onAddProduct(product)}
                        >
                            {/* Phone tiles carry no media — see the @media
                                block in CSS above for why. */}
                            {!isMobile && <TileMedia imageUrl={product.image_url} displayName={name} />}
                            <div className="cbr-tile-name">{name}</div>
                            <div className="cbr-tile-foot">
                                <span className="cbr-tile-price">{currency}{(parseFloat(product.selling_price) || 0).toFixed(2)}</span>
                                <span className="cbr-tile-stock">{pending ? '…' : isOut ? t('out_of_stock') : `×${display}`}</span>
                            </div>
                            {reordering && (
                                <div className="cbr-move">
                                    <button
                                        type="button"
                                        data-testid={`move-up-${product.product_code}`}
                                        aria-label={t('move_up', {}, 'Move up')}
                                        disabled={index === 0}
                                        onClick={(e) => { e.stopPropagation(); move(product.product_code, -1); }}
                                    >&#8593;</button>
                                    <button
                                        type="button"
                                        data-testid={`move-down-${product.product_code}`}
                                        aria-label={t('move_down', {}, 'Move down')}
                                        disabled={index === displayProducts.length - 1}
                                        onClick={(e) => { e.stopPropagation(); move(product.product_code, 1); }}
                                    >&#8595;</button>
                                </div>
                            )}
                        </button>
                    );
                })}
                {displayProducts.length === 0 && (
                    <p className="cbr-none">{t('no_products_in_category')}</p>
                )}
            </div>

            <ManagerGate
                open={reorderGateOpen}
                action="reorder_menu"
                context={{ category: selectedCategory }}
                onApprove={() => { setReorderGateOpen(false); setReordering(true); }}
                onCancel={() => setReorderGateOpen(false)}
            />

            {/* REUSED modifier modal — state lives in the shared useOrderBuilder */}
            <AddonSelectionModal
                isOpen={builder.addonModalOpen}
                onClose={() => builder.setAddonModalOpen(false)}
                currentProduct={builder.currentProduct}
                groupIds={builder.currentProductGroups}
                catalog={builder.activeCatalog}
                tree={builder.modifierTree}
                onToggle={builder.toggleModifier}
                unmet={builder.modifierUnmet}
                flagUnmet={builder.modifierFlagUnmet}
                lineTotal={builder.modifierLineTotal}
                onAdd={builder.addProductWithAddons}
                currency={currency}
            />
        </div>
    );
}
